import { useEffect, useState } from 'react';
import { useYoutubeAudioTracksLazyQuery } from '@/generated/graphql';
import type { AudioTrackOption } from '@/lib/upload/buildAudioSelection';

export type YoutubeAudioTracksStatus =
  | 'idle'
  | 'loading'
  | 'ok'
  | 'unsupported'
  | 'timeout'
  | 'error';

export type YoutubeAudioTracksResult = {
  status: YoutubeAudioTracksStatus;
  tracks: AudioTrackOption[];
};

/** The server waits up to 45 s; the client gives up earlier. */
const CLIENT_TIMEOUT_MS = 20_000;

const IDLE: YoutubeAudioTracksResult = { status: 'idle', tracks: [] };

function narrowKind(kind: string): AudioTrackOption['kind'] {
  return kind === 'original' || kind === 'auto_dub' ? kind : 'other';
}

function narrowStatus(status: string): YoutubeAudioTracksStatus {
  return status === 'ok' || status === 'unsupported' || status === 'timeout' ? status : 'error';
}

/** Looks up the audio tracks of a YouTube video. Stays `idle` until `enabled`
 *  and a URL are both set; `loading` while the lookup runs. */
export function useYoutubeAudioTracks(
  url: string | null,
  enabled: boolean,
): YoutubeAudioTracksResult {
  const [fetchTracks] = useYoutubeAudioTracksLazyQuery({ fetchPolicy: 'network-only' });
  const [state, setState] = useState<{ key: string; result: YoutubeAudioTracksResult } | null>(
    null,
  );
  const active = enabled && !!url;
  const key = active ? (url as string) : '';

  useEffect(() => {
    if (!active) return undefined;
    let done = false;
    // The first outcome wins: cleanup, the timeout, or the response.
    const settle = (result: YoutubeAudioTracksResult) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      setState({ key, result });
    };
    const timer: ReturnType<typeof setTimeout> = setTimeout(() => settle({ status: 'timeout', tracks: [] }), CLIENT_TIMEOUT_MS);
    fetchTracks({ variables: { url: key } })
      .then((res) => {
        const payload = res?.data?.youtubeAudioTracks;
        if (!payload) {
          settle({ status: 'error', tracks: [] });
          return;
        }
        settle({
          status: narrowStatus(payload.status),
          tracks: payload.tracks.map((t) => ({
            code: t.code ?? null,
            language: t.language ?? null,
            kind: narrowKind(t.kind),
            transcribable: t.transcribable,
          })),
        });
      })
      .catch(() => {
        settle({ status: 'error', tracks: [] });
      });
    return () => {
      done = true;
      clearTimeout(timer);
      // A later lookup (same url included) must report `loading`, not this result.
      setState(null);
    };
    // fetchTracks identity is not stable across renders; the lookup is keyed by url.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);

  if (!active) return IDLE;
  if (state && state.key === key) return state.result;
  return { status: 'loading', tracks: [] };
}
